import React, { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, BookOpen, Languages, Pencil } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Components/RefreshToken/RefreshToken';
import './EditCourse.css';
import { MAIN_URL } from '../../constants';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedControl,
  Skeleton,
  StatCard,
  TableSkeleton,
} from '../../Components/ui';
import './CoursesPage.css';
import CourseThumbnail from '../../Components/Courses/CourseThumbnail';

// ── Faqat ko'rsatish uchun yordamchilar ──
const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return null;
  const number = Number(price);
  if (Number.isNaN(number)) return String(price);
  return `${new Intl.NumberFormat('ru-RU').format(number)} so'm`;
};

const getPhotoSrc = (photo) => {
  if (!photo || typeof photo !== 'string') return null;
  return photo.startsWith('http') ? photo : `${MAIN_URL}/${photo}`;
};

const COURSES_PER_PAGE = 10;

const CoursesList = () => {
  const navigate = useNavigate();
  const ACCESS_TOKEN = localStorage.getItem("admin_token");

  const [isActiveCourse, setIsActiveCourse] = useState(true);
  const [allCourses, setAllCourses] = useState([]);   // server'dan kelgan to'liq ro'yxat
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLang, setSelectedLang] = useState('ALL');

  const [deletedIsOpen, setDeletedIsOpen] = useState(false);
  const [coursesId, setCoursesId] = useState(null);
  const [archive, setArchive] = useState(false);

  // ─── Kurslar statistikasi (talabalar soni) ────────────────────────────────
  // Sahifa ochilganda bir marta; filtr/sahifa o'zgarganda qayta chaqirilmaydi.
  // { [courseId]: { students, chapters, completed, notCompleted } }
  const [courseStats, setCourseStats] = useState({ status: "loading", byId: {} });

  useEffect(() => {
    api.get(`/courses/stat`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${ACCESS_TOKEN}`,
      },
    })
      .then((res) => {
        const byId = {};
        (Array.isArray(res?.data) ? res.data : []).forEach((item) => {
          byId[item.id] = {
            students: item.number_of_students,
            chapters: item.number_of_chapters,
            completed: item.completed,
            notCompleted: item.not_completed,
          };
        });
        setCourseStats({ status: "success", byId });
      })
      .catch((err) => {
        console.warn("Kurslar statistikasi yuklanmadi:", err?.response?.status || err?.message);
        setCourseStats({ status: "error", byId: {} });
      });
  }, [ACCESS_TOKEN]);

  // Barcha kurslar bo'yicha yozilishlar yig'indisi (unikal studentlar soni emas).
  const totalEnrollments = Object.values(courseStats.byId).reduce((sum, item) => sum + (Number(item.students) || 0), 0);

  // ─── Kurslarni yuklash ────────────────────────────────────────────────────
  const fetchCourses = () => {
    setLoading(true);
    api.get(`/courses/courses/admin`, {
      params: {
        skip: 0,
        limit: 1000,          // barcha kurslarni bir marta olamiz
        active: isActiveCourse,
      },
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${ACCESS_TOKEN}`,
        "Accept-Language": "uz",
      },
    })
      .then((res) => {
        setAllCourses(res?.data || []);
        setCurrentPage(1);   // yangi filter kelganda 1-sahifaga qayt
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCourses();
  }, [isActiveCourse]);

  // Qidiruv yoki til filteri o'zgarganda 1-sahifaga qayt
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedLang]);

  // ─── Arxiv / Arxivdan chiqarish ───────────────────────────────────────────
  const handleArchiveModal = () => setDeletedIsOpen((x) => !x);

  const archivedCourse = () => {
    api.put(`/courses/change-status/${coursesId}/${archive}`, {}, {
      headers: {
        Authorization: `Bearer ${ACCESS_TOKEN}`,
        Accept: "application/json",
      },
    })
      .then(() => {
        toast.success(
          archive ? "Kurs arxivdan chiqarildi" : "Kurs arxivlandi"
        );
        setDeletedIsOpen(false);
        fetchCourses();          // ro'yxatni yangilash
      })
      .catch((err) => console.error(err));
  };

  // ─── Filter ───────────────────────────────────────────────────────────────
  const getLangCode = (lang = '') => {
    const l = lang.toLowerCase();
    if (l === 'uzbek'   || l === 'uz') return 'uz';
    if (l === 'russian' || l === 'ru') return 'ru';
    if (l === 'english' || l === 'en') return 'en';
    return 'uz';
  };

  const filteredCourses = allCourses.filter((course) => {
    const matchesSearch = course.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const langCode = getLangCode(course.language);
    const matchesLang =
      selectedLang === 'ALL' || langCode === selectedLang.toLowerCase();
    return matchesSearch && matchesLang;
  });

  // ─── Pagination hisob-kitobi ──────────────────────────────────────────────
  const totalPages  = Math.max(1, Math.ceil(filteredCourses.length / COURSES_PER_PAGE));
  const safePage    = Math.min(currentPage, totalPages);   // sahifa yo'qolsa to'g'irlash
  const startIndex  = (safePage - 1) * COURSES_PER_PAGE;
  const endIndex    = startIndex + COURSES_PER_PAGE;
  const currentCourses = filteredCourses.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // ─── Statistika ──────────────────────────────────────────────────────────
  const totalCourses = allCourses.length;
  const uzCourses    = allCourses.filter((c) => getLangCode(c.language) === 'uz').length;
  const ruCourses    = allCourses.filter((c) => getLangCode(c.language) === 'ru').length;
  const enCourses    = allCourses.filter((c) => getLangCode(c.language) === 'en').length;

  // Arxivlash oynasida ko'rsatiladigan kurs (faqat ko'rinish uchun)
  const archiveTarget = allCourses.find((course) => course.id === coursesId);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="ui-courses">
      <PageHeader
        title="Kurslar"
        subtitle="Platformadagi barcha kurslar"
        actions={
          courseStats.status === "success" && (
            <span
              className="ui-courses__enrollments"
              title="Barcha kurslar bo'yicha yozilishlar yig'indisi. Bitta student bir nechta kursda bo'lishi mumkin — bu unikal studentlar soni emas."
            >
              Jami yozilishlar: <strong>{totalEnrollments}</strong>
            </span>
          )
        }
      />

      {/* STAT CARDS */}
      <div className="ui-courses__stats">
        <StatCard icon={<BookOpen />} label="Jami kurslar" value={totalCourses} tone="brand" loading={loading && totalCourses === 0} />
        <StatCard icon={<Languages />} label="O'zbek tilida" value={uzCourses} tone="info" loading={loading && totalCourses === 0} />
        <StatCard icon={<Languages />} label="Rus tilida" value={ruCourses} tone="warning" loading={loading && totalCourses === 0} />
        <StatCard icon={<Languages />} label="Ingliz tilida" value={enCourses} tone="success" loading={loading && totalCourses === 0} />
      </div>

      {/* FILTER */}
      <Card padding="sm">
        <div className="ui-courses__filters">
          <SearchInput
            placeholder="Kurs nomi bo'yicha qidirish..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            wrapperClassName="ui-courses__search"
          />
          <div className="ui-courses__segments">
          <SegmentedControl
            aria-label="Til"
            options={[
              { value: 'ALL', label: 'Hammasi' },
              { value: 'UZ', label: 'UZ' },
              { value: 'RU', label: 'RU' },
              { value: 'EN', label: 'EN' },
            ]}
            value={selectedLang}
            onChange={(lang) => setSelectedLang(lang)}
          />
          {/* ACTIVE / ARCHIVE TOGGLE */}
          <SegmentedControl
            aria-label="Holat"
            options={[
              { value: true, label: 'Faol' },
              { value: false, label: 'Arxiv' },
            ]}
            value={isActiveCourse}
            onChange={(active) => setIsActiveCourse(active)}
          />
          </div>
        </div>
      </Card>

      {/* TABLE */}
      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : currentCourses.length === 0 ? (
        <Card padding="none">
          <EmptyState icon={<BookOpen />} title="Kurslar topilmadi" />
        </Card>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table ui-courses__table">
            <thead>
              <tr>
                <th>Kurs</th>
                <th>Til</th>
                <th className="ui-table-cell--numeric">Narx</th>
                <th className="ui-table-cell--numeric">Talabalar</th>
                <th>Mentor</th>
                <th>Holat</th>
                <th aria-label="Amallar" />
              </tr>
            </thead>
            <tbody>
              {currentCourses.map((course) => {
                const langCode = getLangCode(course.language);
                const photoSrc = getPhotoSrc(course.photo);
                const price = formatPrice(course.price);
                const mentorName = [course.mentor?.first_name, course.mentor?.last_name].filter(Boolean).join(' ');
                return (
                  <tr
                    key={course.id}
                    className="ui-table-row--clickable"
                    onClick={() => navigate("/edit-course", { state: { course } })}
                  >
                    <td>
                      <div className="ui-courses__course">
                        <CourseThumbnail src={photoSrc} className="ui-courses__thumb" />
                        <span className="ui-courses__title" title={course.title}>{course.title}</span>
                      </div>
                    </td>
                    <td>
                      <Badge variant="neutral" size="sm">{langCode.toUpperCase()}</Badge>
                    </td>
                    <td className="ui-table-cell--numeric">
                      {price || <span className="ui-courses__muted">—</span>}
                    </td>
                    <td className="ui-table-cell--numeric">
                      {courseStats.status === "loading" ? (
                        <Skeleton width={40} height={14} radius="sm" className="ui-courses__stat-skeleton" />
                      ) : courseStats.byId[course.id] ? (
                        <div className="ui-courses__students">
                          <span className="ui-courses__students-count">{courseStats.byId[course.id].students}</span>
                          <span className="ui-courses__students-meta">Tugatgan: {courseStats.byId[course.id].completed}</span>
                        </div>
                      ) : (
                        <span className="ui-courses__muted">—</span>
                      )}
                    </td>
                    <td>{mentorName || <span className="ui-courses__muted">—</span>}</td>
                    <td>
                      {isActiveCourse ? (
                        <Badge variant="success" size="sm" dot>Faol</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm" dot>Arxivda</Badge>
                      )}
                    </td>
                    <td className="ui-courses__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Pencil />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/edit-course", { state: { course } });
                        }}
                      >
                        Tahrirlash
                      </Button>
                      {isActiveCourse ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<Archive />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCoursesId(course.id);
                            setArchive(false);
                            handleArchiveModal();
                          }}
                        >
                          Arxivlash
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<ArchiveRestore />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCoursesId(course.id);
                            setArchive(true);
                            handleArchiveModal();
                          }}
                        >
                          Arxivdan chiqarish
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION */}
      {filteredCourses.length > 0 && (
        <div className="ui-courses__footer">
          <span className="ui-courses__muted">
            Jami {filteredCourses.length} tadan {startIndex + 1}–
            {Math.min(endIndex, filteredCourses.length)} ko'rsatilmoqda
          </span>
          <Pagination page={safePage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}

      {/* ARXIV MODALI */}
      <Modal
        open={deletedIsOpen}
        onClose={handleArchiveModal}
        title={isActiveCourse ? "Kursni arxivlaysizmi?" : "Kursni arxivdan chiqarasizmi?"}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={handleArchiveModal}>
              Bekor qilish
            </Button>
            {isActiveCourse ? (
              <Button variant="danger" leftIcon={<Archive />} onClick={archivedCourse}>
                Arxivlash
              </Button>
            ) : (
              <Button leftIcon={<ArchiveRestore />} onClick={archivedCourse}>
                Arxivdan chiqarish
              </Button>
            )}
          </>
        }
      >
        {archiveTarget && <p className="ui-courses__modal-title">{archiveTarget.title}</p>}
        <p className="ui-courses__modal-text">
          {isActiveCourse
            ? "Kurs studentlar uchun yashiriladi. Keyin arxivdan chiqarish mumkin."
            : "Kurs yana studentlar uchun ochiladi."}
        </p>
      </Modal>
    </div>
  );
};

export default CoursesList;